import { type Moment } from 'moment'
import isNil from 'lodash/isNil'
import {
  type GoogleTypeDateInput,
  type InstacartTypesV1BigDecimal,
} from 'domains/aggregate-store-performance/api'
import { MAX_WEEK_OFFSET } from 'domains/aggregate-store-performance/utils/constants/AggregateStorePerformanceOverviewPage.constants'
import { type StoreMetric } from 'domains/aggregate-store-performance/types/AggregateStorePerformance.types'

export const momentToQueryStringDate = (date: Moment): string => date.format('YYYY-MM-DD')

export const toStartOfWeek = (date: Moment): Moment => date.clone().weekday(1)

export const getEndOfWeek = (date: Moment): Moment => date.clone().weekday(7)

export const getFutureCutoff = (now: Moment): Moment =>
  getEndOfWeek(now).add(MAX_WEEK_OFFSET, 'weeks')

export const getMinWeekOffset = (date: Moment, pastCutoff: Moment): number => {
  const now = toStartOfWeek(date)

  return pastCutoff.diff(now, 'weeks')
}

export const isDateOutOfRange = (now: Moment, pastCutoff: Moment) => (date: Moment) => {
  const futureCutoff = getFutureCutoff(now)
  return date.isBefore(pastCutoff, 'day') || date.isAfter(futureCutoff, 'day')
}

export const downloadCsvFile = (fileName: string, data: string) => {
  const urlData = `data:text/csv;charset=UTF-8,${encodeURIComponent(data)}`
  const link = document.createElement('a')
  link.download = fileName
  link.href = urlData

  const event = new MouseEvent('click')
  link.dispatchEvent(event)
  link.remove()
}

export const toQueryDateFormat = (date: Moment): GoogleTypeDateInput => ({
  year: date.year(),
  month: date.month() + 1, // Months are zero indexed in js
  day: date.date(),
})

export const isResponseMetricDefined = (responseMetric: InstacartTypesV1BigDecimal): boolean =>
  !isNil(responseMetric?.units) || !isNil(responseMetric?.nanos)

export const responseMetricToNumber = (
  responseMetric: InstacartTypesV1BigDecimal,
  round = true
): number => {
  if (isResponseMetricDefined(responseMetric)) {
    const number = Number(responseMetric.units) + responseMetric.nanos / Math.pow(10, 9)
    return round ? Math.round(number) : number
  } else {
    return null
  }
}

// matchRate and dedicatedMatchRate are mutually exclusive
export const hasDedicatedRates = (data: StoreMetric[]): boolean =>
  Boolean(data?.length && data[0].dedicatedMatchRate !== undefined)
